// =============================================================================
//  troceaTranscripcion.js
//  DE LÍNEAS DE TRANSCRIPCIÓN A FRAGMENTOS
//
//  Rob, 07-10-2026, pegando el transcript de un vídeo de cinco minutos:
//  «cuenta como 38 líneas y cuando le doy en crear segmentos, como que ignora
//  todo y saca solamente 7... el sistema no es confiable, porque si ya está
//  parseando 38, ¿por qué ignora todo eso y sólo muestra 7, y sobre todo mal
//  posicionado en timeline?».
//
//  ── QUÉ ESTABA PASANDO ──────────────────────────────────────────────────────
//  No ignoraba nada: agrupaba las líneas de SEIS EN SEIS. 42 líneas entre 6 son
//  7 fragmentos. El número estaba escrito en el código, en una constante que
//  decía «fixed, not user-configurable», y en la pantalla no aparecía por
//  ninguna parte.
//
//  O sea que el programa hacía algo razonable y no lo contaba. Y eso, desde
//  fuera, no se distingue de un fallo: se ven 42 líneas, salen 7 fragmentos, y
//  nadie dice por qué. Lo que estaba roto no era la cuenta, era que fuera
//  invisible y que no se pudiera cambiar.
//
//  ── Y LO QUE PASÓ DESPUÉS (07-10-2026, misma tarde) ─────────────────────────
//  Se le puso una casilla para elegir cuántas líneas entraban en cada
//  fragmento. Rob: «eso es basura, yo quiero pasar los datos como son cuando
//  pegue lo real de YouTube... si son 30 cortes o 1000 cortes, conforme lo pegue
//  lo quiero así, tal cual, no quiero agrupar nada».
//
//  Tenía razón otra vez, y la casilla era media respuesta: agrupar de seis en
//  seis no era mejor que agrupar de seis en seis dejándolo elegir. El transcript
//  YA VIENE CORTADO —cada marca de tiempo es un corte que alguien decidió—, y
//  volver a juntarlo para después repartirlo de otra manera es inventarse unos
//  cortes encima de los que ya había.
//
//  Así que no hay número ni casilla: **una marca de tiempo, un fragmento**. Si
//  el transcript trae 38 marcas, salen 38 fragmentos.
//
//  Lo único que no abre fragmento es lo que no es un corte: un verso que el
//  parser separó de un bloque largo —no trae marca propia— y un título de
//  capítulo, que va con el capítulo que presenta.
//
//  ── Y LO QUE SÍ ESTABA MAL ──────────────────────────────────────────────────
//  Cortar cada seis líneas parte por donde toque. En el transcript de Rob eso
//  dejó «Chapter 3: The legendary showdown» EN MITAD del fragmento #4: la
//  pregunta que escriba la IA para ese trozo mezcla el final de un capítulo con
//  el principio del siguiente, y el disparo cae en tierra de nadie.
//
//  Ahora se corta también en los títulos, y el título abre el fragmento que
//  presenta en vez de cerrar el anterior.
//
//  ── POR QUÉ ESTO VIVE SOLO ──────────────────────────────────────────────────
//  Porque es la cuenta que decide cuántos fragmentos salen y dónde empieza cada
//  uno. Equivocarse aquí no se ve: salen fragmentos, con su texto y sus tiempos,
//  y parecen bien. Se mide entera en `audita_troceo.mjs`.
// =============================================================================

//  Un título de capítulo del propio transcript. La regla es a propósito
//  ESTRECHA: vale más no cortar donde había un título que cortar donde no lo
//  había. Un transcript con muchas líneas cortas sin punto final —una canción,
//  una entrevista picada— se convertiría en cien fragmentos de una línea si
//  esto fuera generoso.
//  Por eso detrás de la palabra tiene que venir un NÚMERO o dos puntos. La
//  primera versión se conformaba con un espacio, y entonces «Part of the
//  problem is the map» pasaba por título y partía el vídeo por la mitad de una
//  frase. Un corte de más es peor que uno de menos: el de menos se arregla
//  bajando el número de líneas por fragmento; el de más deja una frase cortada
//  en dos y nadie sabe por qué.
//  Con un número o dos puntos detrás. Sin tocar: es el caso de siempre y un
//  dígito nunca es una palabra, así que no hace falta más freno.
const TITULO_RE =
    /^\s*(chapter|cap[ií]tulo|part|parte|section|secci[óo]n|episode|episodio)\s*(?::|\d+)/i;

//  ── Y EN NÚMEROS ROMANOS ────────────────────────────────────────────────────
//  Aparte, y por dos razones.
//
//  1. VA EN MAYÚSCULAS, sin la `i` de arriba. Las letras romanas son letras: con
//     mayúsculas y minúsculas revueltas, «Parte vi» —«vi», de ver— pasaría por
//     capítulo, y «Part did» también: d, i y d están las tres en la lista.
//
//  2. DETRÁS NO PUEDE VENIR MÁS FRASE, sólo el final de la línea o un signo. Una
//     «I» sola es de las palabras más comunes del inglés: sin esto, «Part I
//     wanted to tell you something» se partía por la mitad como si fuera el
//     título de un capítulo. Un corte de más es peor que uno de menos — el de
//     menos se nota y se arregla; el de más deja una frase en dos y nadie sabe
//     por qué.
//
//  Y hace falta: medio YouTube numera los capítulos así. Antes esta rama no
//  existía de hecho — llevaba dentro un carácter de RETROCESO invisible, un
//  0x08 que se colό al escribir el fichero. En el código se leía `[IVXLCDM]+` y
//  de verdad era `[IVXLCDM]+\x08`, que no puede casar nunca: «Chapter IV»
//  entraba en el vídeo como una frase más del guion. Se veía igual de bien en
//  pantalla, y era mentira.
const TITULO_ROMANO_RE =
    /^\s*(Chapter|Cap[ií]tulo|Part|Parte|Section|Secci[óo]n|Episode|Episodio)\s+[IVXLCDM]+\s*(?:[:.,;—–-]|$)/;

//  ── LO QUE DIGA EL SERVIDOR, PRIMERO (14-10-2026) ───────────────────────────
//  Rob pegó un transcript con rótulos libres —«Intro», «Benefits of being
//  bilingual»— y salieron como fragmentos con tiempo propio:
//
//      #5  0:13 → 0:17   Benefits of being bilingual
//      #6  0:13 → 0:22   It's the mental equivalent of going to a gym every day.
//
//  El servidor YA los había reconocido —los saca a su propia entrada para que la
//  IA no los lea como algo que se dice—, pero los mandaba con la misma forma que
//  una línea hablada, así que aquí había que volver a adivinarlo. Y las dos
//  reglas no eran la misma: la de aquí sólo conoce los «Chapter N».
//
//  Dos reglas para lo mismo se separan solas, y la que menos se mira es la que
//  empieza a mentir. Así que ahora manda la del servidor, que ve el transcript
//  entero, y las de abajo se quedan para cuando no hay servidor: el parser de
//  respaldo del navegador, o un transcript leído de otro sitio.
export const esTitulo = (linea) =>
    linea?.rotulo === true
    || (typeof linea?.text === 'string'
        && (TITULO_RE.test(linea.text) || TITULO_ROMANO_RE.test(linea.text)));

/**
 * Cuánto dura, de media, una línea de este transcript.
 *
 * Hace falta para el final del ÚLTIMO fragmento, que no tiene un siguiente del
 * que copiar el tiempo. Antes se le sumaban diez segundos fijos a la última
 * línea; en un vídeo de frases largas eso corta el final, y en uno de frases
 * sueltas se pasa. La mediana de los huecos de ESTE transcript se parece mucho
 * más a lo que de verdad dura su última línea.
 */
export function huecoTipico(lineas) {
    const tiempos = (lineas || [])
        .map(l => Number(l?.time))
        .filter(t => Number.isFinite(t) && t >= 0)
        .sort((a, b) => a - b);
    const huecos = [];
    for (let i = 1; i < tiempos.length; i++) {
        const d = tiempos[i] - tiempos[i - 1];
        if (d > 0) huecos.push(d);
    }
    if (!huecos.length) return 10;
    huecos.sort((a, b) => a - b);
    const medio = huecos[Math.floor(huecos.length / 2)];
    //  Con topes: una mediana de dos décimas dejaría el último fragmento sin
    //  duración, y una de dos minutos lo estiraría hasta el infinito.
    return Math.min(60, Math.max(4, Math.round(medio)));
}

/**
 * Las líneas, repartidas en fragmentos.
 *
 * @param {Array}  lineas            { text, time, time_ms?, estimado? }
 * @param {number} porFragmento      cuántas líneas como mucho en cada uno
 * @param {bool}   cortarEnTitulos   empezar uno nuevo en cada «Chapter …»
 */
export function trocea(lineas) {
    //  Lo que no es una línea con texto se tira aquí y no más abajo: media
    //  función trabajando con `null` dentro es media función llena de `?.`.
    const todas = (Array.isArray(lineas) ? lineas : [])
        .filter(l => l && typeof l === 'object' && typeof l.text === 'string');
    if (!todas.length) return [];

    //  Una línea «con tiempo propio» es una que traía su marca en el
    //  transcript. Las demás —un verso que el parser separó de un bloque
    //  largo, un título de capítulo— llevan un tiempo deducido, y ésas NO
    //  abren fragmento: no son cortes del vídeo, son trozos de uno.
    //  Un título NUNCA cuenta como marca, aunque venga con tiempo propio. El
    //  parser de respaldo del navegador —el que entra cuando el del servidor no
    //  contesta— no marca cuáles dedujo y le deja al título el tiempo de la
    //  marca ANTERIOR. Sin esta condición, el fragmento del capítulo empezaba
    //  diez segundos antes de su primera frase, encima del anterior:
    //
    //      #6  0:43 → 0:51
    //      #7  0:43 → 1:01     ← los dos empezando en 0:43
    //
    //  Rob lo vio asi, con los dos solapados. No se arregla sólo en el parser
    //  —aunque también se arregla ahí—: esta cuenta no puede depender de que
    //  quien le pase las líneas las traiga bien marcadas.
    const tieneSuTiempo = (l) =>
        Number.isFinite(Number(l?.time)) && l?.estimado !== true && !esTitulo(l);

    //  Si ninguna trae tiempo propio —el parser de respaldo del navegador no
    //  marca `estimado`, o el transcript es raro— cada línea va por su cuenta.
    //  Quedarse sin fragmentos sería peor que partir de más.
    const hayPropios = todas.some(tieneSuTiempo);

    const fragmentos = [];
    let abierto = null;
    //  Los títulos esperan al SIGUIENTE fragmento. En el panel de YouTube el
    //  título va delante de la marca de tiempo del capítulo que presenta, así
    //  que el parser lo deja pegado al bloque ANTERIOR: sin esto, «Chapter 2»
    //  cierra el capítulo 1 en vez de abrir el 2.
    let enEspera = [];

    for (const linea of todas) {
        if (esTitulo(linea)) {
            enEspera.push(linea);
            continue;
        }
        if (!abierto || hayPropios === false || tieneSuTiempo(linea)) {
            abierto = { lines: [...enEspera, linea] };
            enEspera = [];
            fragmentos.push(abierto);
            continue;
        }
        //  Tiempo deducido y no es título: es la continuación de lo abierto.
        abierto.lines.push(linea);
    }
    //  Un título al final no tiene a quién abrir: se queda con el último.
    if (enEspera.length) {
        if (fragmentos.length) fragmentos.at(-1).lines.push(...enEspera);
        else fragmentos.push({ lines: enEspera });
    }

    const cola = huecoTipico(todas);

    return fragmentos.map((f, i) => {
        //  ── EL TÍTULO, FUERA DEL TEXTO ────────────────────────────────
        //  Rob: «sería mejor o eliminarlo, o poner una card de "aquí continúa
        //  el Chapter 2"... sin afectar los tiempos».
        //
        //  Dentro del texto no lo dice nadie —es un rótulo del panel— y la IA
        //  lo lee como si fuera parte de lo hablado al escribir el ejercicio.
        //  Sale a su propio campo: se enseña como separador y no contamina.
        const rotulos = f.lines.filter(esTitulo).map(l => l.text.trim());
        const habladas = f.lines.filter(l => !esTitulo(l));
        //  El fragmento empieza en su línea con tiempo PROPIO. Un título no
        //  trae uno: el parser se lo deduce, y ése cae pegado a la línea
        //  anterior. Tomarlo haría que el fragmento empezara encima del otro.
        const propias = f.lines.filter(tieneSuTiempo);
        const conAlgo = f.lines.filter(l => Number.isFinite(Number(l?.time)));
        const inicio = Number(
            propias[0]?.time ?? conAlgo[0]?.time ?? f.lines[0]?.time ?? 0) || 0;

        const empiezaEn = (g) => {
            const pr = (g?.lines || []).filter(tieneSuTiempo);
            const al = (g?.lines || []).filter(l => Number.isFinite(Number(l?.time)));
            return Number(pr[0]?.time ?? al[0]?.time ?? g?.lines?.[0]?.time);
        };
        const inicioDelSiguiente = fragmentos[i + 1] ? empiezaEn(fragmentos[i + 1]) : null;

        const ultima = Number(conAlgo.at(-1)?.time ?? inicio) || inicio;
        const fin = Number.isFinite(inicioDelSiguiente) && inicioDelSiguiente > inicio
            ? inicioDelSiguiente
            : ultima + cola;

        return {
            id: i,
            lines: f.lines,
            startTime: inicio,
            endTime: fin,
            //  Si el fragmento FUESE sólo el título —un transcript que no trae
            //  más— se queda como texto: mejor eso que un fragmento vacío.
            titulo: rotulos.join(' · ') || undefined,
            text: (habladas.length ? habladas : f.lines).map(l => l.text).join(' '),
            exercises: [],
        };
    });
}

export function cuentaDelTroceo(lineas) {
    const todas = Array.isArray(lineas) ? lineas : [];
    const fragmentos = trocea(todas);
    return {
        lineas: todas.length,
        fragmentos: fragmentos.length,
        titulos: todas.filter(esTitulo).length,
    };
}
